import {
  Component,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  AfterViewInit,
  OnDestroy,
  ElementRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { WidgetEdit } from '../../../../shared/components/widget-edit/widget-edit';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { ModalService } from '../../../../shared/services/modal.service';

interface BudgetCategory {
  id: string;
  name: string;
  icon: string;
  budgetAmount: number;
  usedAmount: number;
  usedPercentage: number;
  color: string;
  status: 'normal' | 'warning' | 'exceeded';
}

@Component({
  selector: 'app-budget-status',
  standalone: true,
  imports: [CommonModule, WidgetEdit, CdkOverlayOrigin],
  templateUrl: './budget-status.html',
  styleUrls: ['./budget-status.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BudgetStatus implements AfterViewInit, OnDestroy {
  // Widget configuration inputs (from dashboard)
  @Input() title = 'Budget Status';
  @Input() size: 'small' | 'medium' | 'half' | 'full' = 'medium';
  @Input() index = 0;
  @Input() isEditMode = false;
  @Input() isAddMode = false;
  @Output() addWidget = new EventEmitter<void>();
  @Input() activeDropdownIndex: number | null = null;
  @Input() getAvailableSizes!: (index: number) => Array<{ value: string; label: string }>;
  @Input() toggleSizeDropdown!: (index: number, event: Event) => void;
  @Input() changeWidgetSize!: (index: number, size: any) => void;
  @Input() removeWidget!: (index: number) => void;

  // Track if widget is being resized
  isResizing = false;
  private resizeObserver?: ResizeObserver;
  private resizeTimeout?: any;

  constructor(
    private modalService: ModalService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef
  ) { }

  ngAfterViewInit() {
    // Set up ResizeObserver to detect when widget is being resized
    this.resizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        // Check if body has is-resizing class (set by dashboard during drag resize)
        const isResizingNow = document.body.classList.contains('is-resizing');

        if (isResizingNow && !this.isResizing) {
          this.isResizing = true;
          this.cdr.markForCheck();
        }

        // Clear the flag after resize completes
        if (this.resizeTimeout) {
          clearTimeout(this.resizeTimeout);
        }

        this.resizeTimeout = setTimeout(() => {
          if (this.isResizing) {
            // Get widget height
            const widgetElement = this.elementRef.nativeElement.closest('.widget');
            if (widgetElement) {
              const height = widgetElement.offsetHeight;

              // If height <= 250px, add back the toggled-full class
              // If height > 250px, keep the class removed
              if (height <= 250) {
                this.isResizing = false;

              } else {
                this.isResizing = true;
              }
              this.cdr.markForCheck();
            }
          }
        }, 300);
      });
    });

    const widgetElement = this.elementRef.nativeElement.closest('.widget');
    if (widgetElement) {
      this.resizeObserver.observe(widgetElement);

      // Check initial height on load
      setTimeout(() => {
        const height = widgetElement.offsetHeight;
        if (height > 250) {
          this.isResizing = true;
          this.cdr.markForCheck();
        }
      }, 100);
    }
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  openModal() {
    if (this.isEditMode || this.isAddMode) return;
    this.modalService.openBudgetStatusModal();
    this.cdr.detectChanges();
  }

  // Hardcoded budget data - Always display only 2 categories
  categories: BudgetCategory[] = [
    {
      id: '1',
      name: 'ALL BUDGET',
      icon: 'assets/icons/img-wallet-01.svg',
      budgetAmount: 70000,
      usedAmount: 28000,
      usedPercentage: 40,
      color: '#26bc8f',
      status: 'normal'
    },
    {
      id: '2',
      name: 'FOOD',
      icon: 'assets/icons/img-star.svg',
      budgetAmount: 70000,
      usedAmount: 84000,
      usedPercentage: 120,
      color: '#ef4d6c',
      status: 'exceeded'
    }
  ];

  /**
   * Get the progress bar width for a category
   */
  getProgressWidth(category: BudgetCategory): string {
    const percentage = Math.min(category.usedPercentage, 100);
    return `${percentage}%`;
  }

  /**
   * Format currency for display
   */
  formatCurrency(amount: number): string {
    return `₹${amount.toLocaleString('en-IN')}`;
  }

  /**
   * Track by function for ngFor optimization
   */
  trackByCategory(index: number, category: BudgetCategory): string {
    return category.id;
  }
}
